/** プロジェクトのRepository。 */

import { getDb } from './schema';
import { enqueue } from './outbox';
import { bumpData } from './events';
import { getDeviceId, newId } from '../domain/ids';
import { nowIsoUtc } from '../lib/datetime';
import { CATEGORY_COLORS } from '../ui/components/CatIcon';
import {
  SCHEMA_VERSION,
  type Milestone,
  type Project,
  type ProjectsDoc,
  type ProjectStatus,
} from '../domain/types';

const KEY = 'projects';

export function emptyProjects(): ProjectsDoc {
  return {
    schemaVersion: SCHEMA_VERSION,
    updatedAt: nowIsoUtc(),
    deviceId: getDeviceId(),
    projects: [],
  };
}

export async function getProjectsDoc(): Promise<ProjectsDoc> {
  const db = await getDb();
  const rec = await db.get('meta', KEY);
  return (rec?.value as ProjectsDoc | undefined) ?? emptyProjects();
}

async function save(doc: ProjectsDoc): Promise<void> {
  const db = await getDb();
  await db.put('meta', {
    key: KEY,
    value: { ...doc, updatedAt: nowIsoUtc(), deviceId: getDeviceId() },
  });
  await enqueue({ type: 'projects.put', targetKey: 'projects' });
  bumpData();
}

export async function putProjectsFromRemote(doc: ProjectsDoc): Promise<void> {
  const db = await getDb();
  await db.put('meta', { key: KEY, value: doc });
  bumpData();
}

export function listProjects(doc: ProjectsDoc, includeArchived = false): Project[] {
  const rank: Record<ProjectStatus, number> = { active: 0, onhold: 1, done: 2 };
  return doc.projects
    .filter((p) => !p.deletedAt)
    .filter((p) => includeArchived || !p.archived)
    .sort((a, b) => {
      if (rank[a.status] !== rank[b.status]) return rank[a.status] - rank[b.status];
      // 締め切りが近い順。未設定は後ろ
      if (a.deadline !== b.deadline) {
        if (!a.deadline) return 1;
        if (!b.deadline) return -1;
        return a.deadline < b.deadline ? -1 : 1;
      }
      return a.name.localeCompare(b.name, 'ja');
    });
}

export function findProject(doc: ProjectsDoc, id: string | null | undefined): Project | undefined {
  if (!id) return undefined;
  const p = doc.projects.find((x) => x.id === id);
  return p && !p.deletedAt ? p : undefined;
}

export async function addProject(input: {
  name: string;
  description?: string;
  deadline?: string | null;
  color?: string;
}): Promise<string> {
  const doc = await getProjectsDoc();
  const id = newId();
  const now = nowIsoUtc();
  await save({
    ...doc,
    projects: [
      ...doc.projects,
      {
        id,
        name: input.name,
        color: input.color ?? CATEGORY_COLORS[doc.projects.length % CATEGORY_COLORS.length],
        description: input.description ?? '',
        status: 'active',
        deadline: input.deadline ?? null,
        manualProgress: 0,
        milestones: [],
        createdAt: now,
        updatedAt: now,
        archived: false,
        deletedAt: null,
      },
    ],
  });
  return id;
}

export async function updateProject(id: string, patch: Partial<Project>): Promise<void> {
  const doc = await getProjectsDoc();
  await save({
    ...doc,
    projects: doc.projects.map((p) =>
      p.id === id ? { ...p, ...patch, id: p.id, updatedAt: nowIsoUtc() } : p,
    ),
  });
}

/**
 * 削除。レコード自体は残し、deletedAt を立てる。
 * 物理削除にすると、同期のマージでリモート側の一覧から復活してしまう。
 */
export async function deleteProject(id: string): Promise<void> {
  await updateProject(id, { deletedAt: nowIsoUtc() });
}

export async function restoreProject(id: string): Promise<void> {
  await updateProject(id, { deletedAt: null });
}

export function listDeletedProjects(doc: ProjectsDoc): Project[] {
  return doc.projects
    .filter((p) => p.deletedAt)
    .sort((a, b) => ((a.deletedAt ?? '') < (b.deletedAt ?? '') ? 1 : -1));
}

/* ---------------- マイルストーン ---------------- */

async function withMilestones(
  projectId: string,
  fn: (ms: Milestone[]) => Milestone[],
): Promise<void> {
  const doc = await getProjectsDoc();
  const p = doc.projects.find((x) => x.id === projectId);
  if (!p) return;
  await updateProject(projectId, { milestones: fn([...p.milestones]) });
}

export async function addMilestone(projectId: string, title: string, due: string | null) {
  await withMilestones(projectId, (ms) => [
    ...ms,
    {
      id: newId(),
      title,
      due,
      done: false,
      doneAt: null,
      order: Math.max(0, ...ms.map((m) => m.order)) + 1,
    },
  ]);
}

export async function updateMilestone(projectId: string, id: string, patch: Partial<Milestone>) {
  await withMilestones(projectId, (ms) => ms.map((m) => (m.id === id ? { ...m, ...patch } : m)));
}

export async function toggleMilestone(projectId: string, id: string, done: boolean) {
  await withMilestones(projectId, (ms) =>
    ms.map((m) => (m.id === id ? { ...m, done, doneAt: done ? nowIsoUtc() : null } : m)),
  );
}

export async function removeMilestone(projectId: string, id: string) {
  await withMilestones(projectId, (ms) => ms.filter((m) => m.id !== id));
}

export async function moveMilestone(projectId: string, id: string, delta: number) {
  await withMilestones(projectId, (ms) => {
    const sorted = [...ms].sort((a, b) => a.order - b.order);
    const i = sorted.findIndex((m) => m.id === id);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= sorted.length) return ms;
    [sorted[i], sorted[j]] = [sorted[j], sorted[i]];
    return sorted.map((m, n) => ({ ...m, order: n + 1 }));
  });
}
